import * as XLSX from 'xlsx';
import { ImportModelType, ImportTemplate, TemplateColumn } from '@/types/import';
import { supabase } from '@/integrations/supabase/client';

// Function to fetch real data for templates
async function fetchRealDataForTemplates() {
  try {
    const [
      { data: categories },
      { data: people },
      { data: peopleRoles },
      { data: books },
      { data: languages },
    ] = await Promise.all([
      supabase.from('categories').select('name, name_mz, icon').limit(3),
      supabase.from('people').select('name, email, bio').limit(3),
      supabase.from('people_roles').select('name').limit(10),
      supabase.from('books').select('title, reading_time_minutes, rating').limit(3),
      supabase.from('languages').select('name, code').limit(5),
    ]);

    return {
      categories: categories || [],
      people: people || [],
      peopleRoles: (peopleRoles || []).map(r => r.name),
      books: books || [],
      languages: languages || [],
    };
  } catch (error) {
    console.warn('Failed to fetch real data for templates, using defaults:', error);
    return null;
  }
}

// Template configurations for each model type
export const TEMPLATE_CONFIGS: Record<ImportModelType, ImportTemplate> = {
  categories: {
    modelType: 'categories',
    fileName: 'categories_import_template.xlsx',
    sheets: [{
      name: 'Categories',
      headers: [
        {
          name: 'ID',
          databaseField: 'id',
          isRequired: false,
          dataType: 'number',
          description: 'Optional. Leave blank for new categories, or provide existing ID to update',
          example: '1',
        },
        {
          name: 'Name',
          databaseField: 'name',
          isRequired: true,
          dataType: 'string',
          description: 'Category name (required)',
          example: 'Self-Help',
        },
        {
          name: 'Name (Mizo)',
          databaseField: 'name_mz',
          isRequired: false,
          dataType: 'string',
          description: 'Category name in Mizo language (optional)',
          example: 'Infihlim',
        },
        {
          name: 'Icon',
          databaseField: 'icon',
          isRequired: false,
          dataType: 'string',
          description: 'Icon name or URL (optional)',
          example: 'book-open',
        },
        {
          name: 'Position',
          databaseField: 'position',
          isRequired: false,
          dataType: 'number',
          description: 'Display order position (optional)',
          example: '1',
        },
        {
          name: 'Is Active',
          databaseField: 'is_active',
          isRequired: false,
          dataType: 'boolean',
          description: 'Whether category is active (true/false, default: true)',
          example: 'true',
        },
      ],
      sampleData: [
        {
          'ID': '',
          'Name': 'Self-Help',
          'Name (Mizo)': 'Infihlim',
          'Icon': 'lightbulb',
          'Position': 1,
          'Is Active': true,
        },
        {
          'ID': '',
          'Name': 'Business',
          'Name (Mizo)': 'Sumdawnna',
          'Icon': 'briefcase',
          'Position': 2,
          'Is Active': true,
        },
      ],
      validationRules: [
        'Name is required and must be unique',
        'Position should be a positive number',
        'Is Active should be true or false',
      ],
      notes: [
        'Leave ID blank to create new categories',
        'Provide ID to update existing categories',
        'Name must be unique across all categories',
        'Position determines display order (lower numbers appear first)',
      ],
    }],
  },

  people: {
    modelType: 'people',
    fileName: 'people_import_template.xlsx',
    sheets: [{
      name: 'People',
      headers: [
        {
          name: 'ID',
          databaseField: 'id',
          isRequired: false,
          dataType: 'number',
          description: 'Optional. Leave blank for new people, or provide existing ID to update',
          example: '1',
        },
        {
          name: 'Name',
          databaseField: 'name',
          isRequired: true,
          dataType: 'string',
          description: 'Person\'s full name (required)',
          example: 'John Doe',
        },
        {
          name: 'Email',
          databaseField: 'email',
          isRequired: false,
          dataType: 'string',
          description: 'Email address (optional)',
          example: 'john.doe@example.com',
          validation: '^[^@]+@[^@]+\\.[^@]+$',
        },
        {
          name: 'Bio',
          databaseField: 'bio',
          isRequired: false,
          dataType: 'string',
          description: 'Biography or description (optional)',
          example: 'Bestselling author and motivational speaker',
        },
        {
          name: 'Avatar URL',
          databaseField: 'avatarUrl',
          isRequired: false,
          dataType: 'string',
          description: 'Profile picture URL (optional)',
          example: 'https://example.com/avatar.jpg',
        },
        {
          name: 'Roles',
          databaseField: 'people_to_roles',
          isRequired: false,
          dataType: 'string',
          description: 'Comma-separated role names (optional)',
          example: 'Author, Speaker',
          foreignKeyInfo: {
            table: 'people_roles',
            column: 'name',
            description: 'Must match existing role names (Author, Narrator, Translator)',
          },
        },
      ],
      sampleData: [
        {
          'ID': '',
          'Name': 'Dale Carnegie',
          'Email': 'dale@example.com',
          'Bio': 'American writer and lecturer, developer of courses in self-improvement',
          'Avatar URL': '',
          'Roles': 'Author',
        },
        {
          'ID': '',
          'Name': 'Stephen Covey',
          'Email': 'stephen@example.com',
          'Bio': 'Author of The 7 Habits of Highly Effective People',
          'Avatar URL': '',
          'Roles': 'Author, Speaker',
        },
      ],
      validationRules: [
        'Name is required and must be unique',
        'Email must be valid format if provided',
        'Roles should be comma-separated existing role names',
      ],
      notes: [
        'Leave ID blank to create new people',
        'Provide ID to update existing people',
        'Name must be unique across all people',
        'Roles will be matched against existing role names',
        'Invalid role names will be ignored with a warning',
      ],
    }],
  },

  books: {
    modelType: 'books',
    fileName: 'books_import_template.xlsx',
    sheets: [{
      name: 'Books',
      headers: [
        {
          name: 'ID',
          databaseField: 'id',
          isRequired: false,
          dataType: 'number',
          description: 'Optional. Leave blank for new books, or provide existing ID to update',
          example: '1',
        },
        {
          name: 'Title',
          databaseField: 'title',
          isRequired: true,
          dataType: 'string',
          description: 'Book title (required)',
          example: 'How to Win Friends and Influence People',
        },
        {
          name: 'Cover Image',
          databaseField: 'cover_image',
          isRequired: false,
          dataType: 'string',
          description: 'Cover image URL (optional)',
          example: 'https://example.com/cover.jpg',
        },
        {
          name: 'Cover Background Color',
          databaseField: 'cover_bg_color',
          isRequired: false,
          dataType: 'string',
          description: 'Hex color code for cover background (optional)',
          example: '#3B82F6',
        },
        {
          name: 'Reading Time',
          databaseField: 'reading_time',
          isRequired: false,
          dataType: 'number',
          description: 'Estimated reading time in minutes (optional)',
          example: '45',
        },
        {
          name: 'Rating',
          databaseField: 'rating',
          isRequired: false,
          dataType: 'number',
          description: 'Book rating (0-5, optional)',
          example: '4.5',
        },
        {
          name: 'Is Active',
          databaseField: 'is_active',
          isRequired: false,
          dataType: 'boolean',
          description: 'Whether book is active (true/false, default: true)',
          example: 'true',
        },
        {
          name: 'Authors',
          databaseField: 'authors',
          isRequired: false,
          dataType: 'string',
          description: 'Comma-separated author names (must match existing people)',
          example: 'Dale Carnegie, John Maxwell',
          foreignKeyInfo: {
            table: 'people',
            column: 'name',
            description: 'Must match existing person names exactly (case-insensitive)',
          },
        },
        {
          name: 'Author Bios',
          databaseField: 'author_bios',
          isRequired: false,
          dataType: 'string',
          description: 'Pipe-separated (|) per-book bios matching author order. Optional.',
          example: 'Psychotherapist and lecturer|Habits researcher',
        },
        {
          name: 'Categories',
          databaseField: 'categories',
          isRequired: false,
          dataType: 'string',
          description: 'Comma-separated category names (must match existing categories)',
          example: 'Self-Help, Business',
          foreignKeyInfo: {
            table: 'categories',
            column: 'name',
            description: 'Must match existing category names exactly (case-insensitive)',
          },
        },
      ],
      sampleData: [
        {
          'ID': '',
          'Title': 'How to Win Friends and Influence People',
          'Cover Image': '',
          'Cover Background Color': '#3B82F6',
          'Reading Time': 45,
          'Rating': 4.8,
          'Is Active': true,
          'Authors': 'Dale Carnegie',
          'Author Bios': 'American writer and developer of self-improvement courses',
          'Categories': 'Self-Help, Communication',
        },
        {
          'ID': '',
          'Title': 'The 7 Habits of Highly Effective People',
          'Cover Image': '',
          'Cover Background Color': '#10B981',
          'Reading Time': 60,
          'Rating': 4.7,
          'Is Active': true,
          'Authors': 'Stephen Covey, John Maxwell',
          'Author Bios': 'Author of The 7 Habits of Highly Effective People|Leadership author and speaker',
          'Categories': 'Self-Help, Leadership',
        },
      ],
      validationRules: [
        'Title is required',
        'Reading Time should be a positive number in minutes',
        'Rating should be between 0 and 5',
        'Authors must reference existing people (comma-separated)',
        'Author Bios should be pipe-separated (|) values matching author order',
        'Categories must reference existing categories (comma-separated)',
      ],
      notes: [
        'Leave ID blank to create new books',
        'Provide ID to update existing books',
        'Authors will be matched against existing people names (case-insensitive)',
        'Author Bios are matched to authors by position (1st bio → 1st author)',
        'Use pipe (|) to separate multiple bios, leave empty for no bio',
        'Categories will be matched against existing category names (case-insensitive)',
        'Missing authors/categories will be logged as warnings but won\'t fail the import',
        'For updates, existing author/category relationships will be replaced',
        'Make sure to create people and categories before importing books',
      ],
    }],
  },

  summary_contents: {
    modelType: 'summary_contents',
    fileName: 'summary_contents_import_template.xlsx',
    sheets: [{
      name: 'Summary Contents',
      headers: [
        {
          name: 'ID',
          databaseField: 'id',
          isRequired: false,
          dataType: 'number',
          description: 'Optional. Leave blank for new content, or provide existing ID to update',
          example: '1',
        },
        {
          name: 'Book Title',
          databaseField: 'book_title',
          isRequired: true,
          dataType: 'string',
          description: 'Book title to link this content to (required)',
          example: 'How to Win Friends and Influence People',
          foreignKeyInfo: {
            table: 'books',
            column: 'title',
            description: 'Must match an existing book title',
          },
        },
        {
          name: 'Language',
          databaseField: 'language',
          isRequired: true,
          dataType: 'string',
          description: 'Language code (required)',
          example: 'en',
        },
        {
          name: 'Content Type',
          databaseField: 'content_type',
          isRequired: false,
          dataType: 'string',
          description: 'Edition type: "summary" or "full_book" (default: summary)',
          example: 'summary',
        },
        {
          name: 'Subtitle',
          databaseField: 'subtitle',
          isRequired: false,
          dataType: 'string',
          description: 'Content subtitle (optional)',
          example: 'A comprehensive summary',
        },
        {
          name: 'Short Description',
          databaseField: 'short_description',
          isRequired: false,
          dataType: 'string',
          description: 'Brief description of the content (optional)',
          example: 'Learn the fundamental principles of human relations...',
        },
        {
          name: 'Audio Title',
          databaseField: 'audio_title',
          isRequired: false,
          dataType: 'string',
          description: 'Audio version title (optional)',
          example: 'How to Win Friends - Audio Summary',
        },
        {
          name: 'Audio Narrator',
          databaseField: 'audio_narrator',
          isRequired: false,
          dataType: 'string',
          description: 'Narrator name (optional)',
          example: 'John Smith',
          foreignKeyInfo: {
            table: 'people',
            column: 'name',
            description: 'Must match an existing person name',
          },
        },
        {
          name: 'Audio Duration',
          databaseField: 'audio_duration',
          isRequired: false,
          dataType: 'number',
          description: 'Audio duration in seconds (optional)',
          example: '2700',
        },
        {
          name: 'Audio URL',
          databaseField: 'audio_url',
          isRequired: false,
          dataType: 'string',
          description: 'Audio file URL (optional)',
          example: 'https://example.com/audio.mp3',
        },
        {
          name: 'Translator',
          databaseField: 'translator',
          isRequired: false,
          dataType: 'string',
          description: 'Translator name (optional)',
          example: 'Jane Doe',
          foreignKeyInfo: {
            table: 'people',
            column: 'name',
            description: 'Must match an existing person name',
          },
        },
        {
          name: 'Position',
          databaseField: 'position',
          isRequired: false,
          dataType: 'number',
          description: 'Display order position (optional)',
          example: '1',
        },
      ],
      sampleData: [
        {
          'ID': '',
          'Book Title': 'How to Win Friends and Influence People',
          'Language': 'en',
          'Content Type': 'summary',
          'Subtitle': 'Complete Summary',
          'Short Description': 'A comprehensive guide to building meaningful relationships and influencing others positively.',
          'Audio Title': 'How to Win Friends - Audio Summary',
          'Audio Narrator': '',
          'Audio Duration': 2700,
          'Audio URL': '',
          'Translator': '',
          'Position': 1,
        },
      ],
      validationRules: [
        'Book Title must reference an existing book',
        'Language is required',
        'Content Type must be "summary" or "full_book" (defaults to "summary" if omitted)',
        'Audio Narrator and Translator must reference existing people if provided',
        'Audio Duration should be in seconds',
      ],
      notes: [
        'IMPORTANT: Before importing summary contents, ensure the book exists',
        'Book Title must exactly match an existing book title',
        'Each book can have multiple editions for different languages and content types',
        'Content Type: "summary" for condensed summaries, "full_book" for complete book content',
        'Import order: Books → Summary Contents → Chapters',
        'Audio Narrator and Translator will be matched against existing people',
        'Position determines display order within the same book',
      ],
    }],
  },

  chapters: {
    modelType: 'chapters',
    fileName: 'chapters_import_template.xlsx',
    sheets: [{
      name: 'Chapters',
      headers: [
        {
          name: 'ID',
          databaseField: 'id',
          isRequired: false,
          dataType: 'number',
          description: 'Optional. Leave blank for new chapters, or provide existing ID to update',
          example: '1',
        },
        {
          name: 'Book Title',
          databaseField: 'book_title',
          isRequired: true,
          dataType: 'string',
          description: 'Book title to link this chapter to (required)',
          example: 'How to Win Friends and Influence People',
          foreignKeyInfo: {
            table: 'books',
            column: 'title',
            description: 'Must match an existing book title',
          },
        },
        {
          name: 'Language',
          databaseField: 'language',
          isRequired: true,
          dataType: 'string',
          description: 'Language code (required)',
          example: 'en',
        },
        {
          name: 'Label',
          databaseField: 'label',
          isRequired: true,
          dataType: 'string',
          description: 'Chapter label (required)',
          example: 'Chapter 1',
        },
        {
          name: 'Title',
          databaseField: 'title',
          isRequired: true,
          dataType: 'string',
          description: 'Chapter title (required)',
          example: 'Fundamental Techniques in Handling People',
        },
        {
          name: 'Content',
          databaseField: 'content',
          isRequired: true,
          dataType: 'string',
          description: 'Chapter content (required)',
          example: 'The principles outlined in this chapter...',
        },
        {
          name: 'Start Time',
          databaseField: 'start_time',
          isRequired: false,
          dataType: 'number',
          description: 'Audio start time in seconds (optional)',
          example: '120',
        },
        {
          name: 'Position',
          databaseField: 'position',
          isRequired: false,
          dataType: 'number',
          description: 'Chapter order position (optional)',
          example: '1',
        },
      ],
      sampleData: [
        {
          'ID': '',
          'Book Title': 'How to Win Friends and Influence People',
          'Language': 'en',
          'Label': 'Chapter 1',
          'Title': 'Fundamental Techniques in Handling People',
          'Content': 'The principles outlined in this chapter focus on the fundamental approaches to building positive relationships with others...',
          'Start Time': 0,
          'Position': 1,
        },
        {
          'ID': '',
          'Book Title': 'How to Win Friends and Influence People',
          'Language': 'en',
          'Label': 'Chapter 2',
          'Title': 'Six Ways to Make People Like You',
          'Content': 'This chapter explores practical methods for creating genuine connections and making positive first impressions...',
          'Start Time': 300,
          'Position': 2,
        },
      ],
      validationRules: [
        'Book Title must reference an existing book',
        'Language is required and must match a summary content',
        'Label, Title, and Content are required',
        'Start Time should be in seconds',
        'Position should be unique within the same summary content',
      ],
      notes: [
        'IMPORTANT: Before importing chapters, ensure the book exists and has summary content in the specified language',
        'Book Title and Language combination must match an existing summary content',
        'Import order: Books → Summary Contents → Chapters',
        'Chapters will be linked to the appropriate summary content automatically',
        'Position determines chapter order within the book',
        'Start Time is used for audio synchronization',
      ],
    }],
  },
};

// Function to update sample data with real data
function createUpdatedSampleData(
  modelType: ImportModelType, 
  originalSampleData: any[], 
  realData: any
): any[] {
  switch (modelType) {
    case 'categories':
      return realData.categories.slice(0, 2).map((cat: any, index: number) => ({
        'ID': '',
        'Name': cat.name || `Category ${index + 1}`,
        'Name (Mizo)': cat.name_mz || '',
        'Icon': cat.icon || 'folder',
        'Position': index + 1,
        'Is Active': true,
      }));

    case 'people':
      return realData.people.slice(0, 2).map((person: any) => ({
        'ID': '',
        'Name': person.name || 'Sample Person',
        'Email': person.email || 'person@example.com',
        'Bio': person.bio || 'Sample biography',
        'Avatar URL': '',
        'Roles': realData.peopleRoles.slice(0, 2).join(', ') || 'Author',
      }));

    case 'books':
      return realData.books.slice(0, 2).map((book: any) => ({
        'ID': '',
        'Title': book.title || 'Sample Book Title',
        'Cover Image': '',
        'Cover Background Color': '',
        'Reading Time': book.reading_time_minutes || 45,
        'Rating': book.rating || 4.5,
        'Is Active': true,
        'Authors': realData.people.slice(0, 1).map((p: any) => p.name).join(', ') || 'Sample Author',
        'Author Bios': '',
        'Categories': realData.categories.slice(0, 1).map((c: any) => c.name).join(', ') || 'Sample Category',
      }));

    case 'summary_contents':
      return realData.books.slice(0, 2).map((book: any, index: number) => ({
        'ID': '',
        'Book Title': book.title || `Sample Book ${index + 1}`,
        'Language': realData.languages[index]?.code || 'en',
        'Content Type': index === 0 ? 'summary' : 'full_book',
        'Subtitle': `Summary of ${book.title || 'Sample Book'}`,
        'Short Description': `A comprehensive summary of ${book.title || 'the book'}...`,
        'Audio Title': '',
        'Audio Narrator': realData.people.slice(0, 1).map((p: any) => p.name).join(', ') || '',
        'Audio Duration': (book.reading_time_minutes || 45) * 60,
        'Audio URL': '',
        'Translator': '',
        'Position': index + 1,
      }));

    case 'chapters':
      return realData.books.slice(0, 1).flatMap((book: any) => [
        {
          'ID': '',
          'Book Title': book.title || 'Sample Book',
          'Language': realData.languages[0]?.code || 'en',
          'Label': 'Chapter 1',
          'Title': 'Introduction',
          'Content': `This is the opening chapter of ${book.title || 'the book'}...`,
          'Start Time': 0,
          'Position': 1,
        },
        {
          'ID': '',
          'Book Title': book.title || 'Sample Book',
          'Language': realData.languages[0]?.code || 'en',
          'Label': 'Chapter 2',
          'Title': 'Main Concepts',
          'Content': `This chapter covers the main concepts of ${book.title || 'the book'}...`,
          'Start Time': 300,
          'Position': 2,
        },
      ]);

    default:
      return originalSampleData;
  }
}

export async function generateExcelTemplate(modelType: ImportModelType): Promise<ArrayBuffer> {
  // Fetch real data to update sample data
  const realData = await fetchRealDataForTemplates();
  
  const template = TEMPLATE_CONFIGS[modelType];
  const workbook = XLSX.utils.book_new();

  template.sheets.forEach((sheetConfig) => {
    // Create headers row
    const headers = sheetConfig.headers.map(col => col.name);
    
    // Create updated sample data with real data when available
    let sampleData = sheetConfig.sampleData;
    if (realData) {
      sampleData = createUpdatedSampleData(modelType, sheetConfig.sampleData, realData);
    }
    
    // Create sample data rows
    const data = [headers, ...sampleData.map(row => 
      headers.map(header => row[header] ?? '')
    )];

    // Create worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(data);

    // Set column widths
    const columnWidths = headers.map(header => {
      const maxLength = Math.max(
        header.length,
        ...sampleData.map(row => 
          String(row[header] ?? '').length
        )
      );
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });
    worksheet['!cols'] = columnWidths;

    // Add validation notes as comments (if supported)
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    
    // Style the header row
    for (let col = 0; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellAddress]) continue;
      
      worksheet[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F46E5" } },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }

    // Add the sheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetConfig.name);

    // Add instructions sheet
    const instructionsData = [
      ['Import Instructions'],
      [''],
      ['Validation Rules:'],
      ...sheetConfig.validationRules.map(rule => [rule]),
      [''],
      ['Important Notes:'],
      ...sheetConfig.notes.map(note => [note]),
      [''],
      ['Column Descriptions:'],
      ['Column Name', 'Required', 'Data Type', 'Description', 'Example'],
      ...sheetConfig.headers.map(col => [
        col.name,
        col.isRequired ? 'Yes' : 'No',
        col.dataType,
        col.description,
        col.example
      ])
    ];

    const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
    instructionsSheet['!cols'] = [
      { wch: 20 }, { wch: 10 }, { wch: 12 }, { wch: 50 }, { wch: 30 }
    ];

    XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');
  });

  // Generate buffer
  return XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array',
    cellStyles: true
  });
}

export async function downloadTemplate(modelType: ImportModelType): Promise<void> {
  const template = TEMPLATE_CONFIGS[modelType];
  const buffer = await generateExcelTemplate(modelType);
  
  // Create blob and download
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = template.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
} 